// Query builder that works directly off EmployeeQuery ({ and: [{ or: [condition] }] }).
// Two-column layout: editor (left) for building conditions, cards (right) for saved AND-groups.
import React, { useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

import type { IconDefinition } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Chip, PageBanner } from "@performyard/py-components";

import { ConditionCard } from "./ConditionCard";
import { ConditionEditor } from "./ConditionEditor";

import type { EmployeeQuery, IEmployeeField, QueryCondition } from "./types";

type EmployeeQueryBuilderProps = {
	employeeFields: IEmployeeField[];
	value: EmployeeQuery | null;
	onChange: (query: EmployeeQuery) => void;
	enableAnniversary?: boolean;
	fieldSelectPlaceholder?: string;
	errorBanner?: { message: string; icon: IconDefinition };
};

export const EmployeeQueryBuilder = ({
	employeeFields,
	value,
	onChange,
	enableAnniversary = false,
	fieldSelectPlaceholder,
	errorBanner,
}: EmployeeQueryBuilderProps) => {
	const [editingAndIndex, setEditingAndIndex] = useState<number | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const andGroups = value?.and ?? [];

	const handleSave = (conditions: QueryCondition[]) => {
		const newAndGroup = { or: conditions };
		let newAnd: EmployeeQuery["and"];

		if (editingAndIndex !== null) {
			newAnd = andGroups.map((group, i) => (i === editingAndIndex ? newAndGroup : group));
			setEditingAndIndex(null);
		} else {
			newAnd = [...andGroups, newAndGroup];
		}

		onChange({ and: newAnd });
	};

	const handleRemove = (index: number) => {
		if (editingAndIndex === index) {
			setEditingAndIndex(null);
		}
		const newAnd = andGroups.filter((_, i) => i !== index);
		onChange({ and: newAnd });
	};

	const handleCardClick = (index: number) => {
		setEditingAndIndex(index);
	};

	useOnClickOutside(containerRef as React.RefObject<HTMLElement>, (event) => {
		if (editingAndIndex === null) return;
		const target = event.target as HTMLElement;
		const isPortalClick = target.closest(
			"[role='listbox'], [data-radix-popper-content-wrapper], [data-state='open']"
		);
		if (isPortalClick) return;
		setEditingAndIndex(null);
	});

	const editingConditions = editingAndIndex !== null ? andGroups[editingAndIndex]?.or : undefined;

	return (
		<div className="flex justify-center">
			<div className="w-full max-w-[792px]" ref={containerRef}>
				{errorBanner && (
					<div className="mb-4 w-fit">
						<PageBanner
							color="red"
							mainTitle={errorBanner.message}
							icon={errorBanner.icon}
							dangerouslySetClassName="!p-1.5"
						/>
					</div>
				)}
				<div className="flex justify-between gap-4">
					<div className="min-w-0 max-w-[312px] flex-1">
						<ConditionEditor
							employeeFields={employeeFields}
							onSave={handleSave}
							editingConditions={editingConditions}
							enableAnniversary={enableAnniversary}
							fieldSelectPlaceholder={fieldSelectPlaceholder}
						/>
					</div>

					<div className="min-w-0 max-w-[424px] flex-1">
						{andGroups.map((group, index) => (
							<div key={index}>
								<ConditionCard
									conditions={group.or}
									employeeFields={employeeFields}
									onRemove={() => handleRemove(index)}
									onClick={() => handleCardClick(index)}
									isSelected={editingAndIndex === index}
								/>
								{index < andGroups.length - 1 && (
									<div className="my-2">
										<Chip label="AND" color="neutral" size={24} />
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};
